import React, { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';

const MAIN_SECTIONS = [
  { id: "preboda", title: "Preboda" },
  { id: "ceremonia", title: "Ceremonia" },
  { id: "celebracion", title: "Celebración" }
];

const SECONDARY_SECTIONS = [
  { id: "recomendaciones", title: "Recomendaciones" },
  { id: "regalos", title: "Detalles" }
];

const Navbar = () => {
  const [activeSection, setActiveSection] = useState("inicio");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Update navbar style on scroll
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // Find which section is currently in view
      const sections = [
        { id: "inicio", element: document.getElementById("inicio") },
        ...MAIN_SECTIONS.map(section => ({
          id: section.id,
          element: document.getElementById(section.id),
        })),
        ...SECONDARY_SECTIONS.map(section => ({
          id: section.id,
          element: document.getElementById(section.id),
        }))
      ].filter(section => section.element !== null);

      const current = sections.find(section => {
        const element = section.element;
        if (!element) return false;
        
        const rect = element.getBoundingClientRect();
        return rect.top <= 150 && rect.bottom >= 150;
      });

      if (current) {
        setActiveSection(current.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300
      ${isMenuOpen || isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}
    >
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex items-center justify-between py-4">
          {/* Left section - Main Navigation Links */}
          <div className="hidden md:flex items-center space-x-8 lg:space-x-16">
            {MAIN_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`text-xs md:text-sm transition-colors duration-300 font-outfit
                  ${activeSection === section.id 
                    ? 'text-wedding-burgundy font-medium' 
                    : isMenuOpen || isScrolled ? 'text-gray-700 hover:text-wedding-burgundy' : 'text-white hover:text-wedding-burgundy'}`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Center - Logo */}
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => scrollToSection("inicio")}
              className={`font-playfair font-medium md:text-lg transition-colors duration-300 cursor-pointer
                ${isMenuOpen || isScrolled ? 'text-wedding-burgundy' : 'text-white'}`}
            >
              V & S
            </button>
          </div>

          {/* Right section - Secondary Links and RSVP */}
          <div className="hidden md:flex items-center space-x-8 lg:space-x-16">
            {SECONDARY_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`text-xs md:text-sm transition-colors duration-300 font-outfit
                  ${activeSection === section.id 
                    ? 'text-wedding-burgundy font-medium' 
                    : isMenuOpen || isScrolled ? 'text-gray-700 hover:text-wedding-burgundy' : 'text-white hover:text-wedding-burgundy'}`}
              >
                {section.title}
              </button>
            ))}
            <div className="ml-8 lg:ml-16">
              <button
                onClick={() => scrollToSection("rsvp")}
                className={`px-4 py-2 text-xs md:text-sm font-medium rounded-md transition-colors duration-300 font-outfit
                  ${isScrolled 
                    ? 'text-white bg-wedding-burgundy hover:bg-wedding-burgundy/90' 
                    : 'text-wedding-burgundy bg-white/90 backdrop-blur-sm hover:bg-white'}`}
              >
                RSVP
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <button 
            className={`md:hidden transition-colors duration-300 font-outfit
              ${isMenuOpen || isScrolled ? 'text-wedding-burgundy' : 'text-white'}`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menú"
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/95 border-t border-gray-200 shadow-md animate-fade-in font-outfit">
          <div className="max-w-7xl mx-auto px-8 py-2">
            <div className="space-y-1">
              {[...MAIN_SECTIONS, ...SECONDARY_SECTIONS].map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`block w-full text-left py-3 px-4 text-sm transition-colors rounded-md font-outfit
                    ${activeSection === section.id 
                      ? 'text-wedding-burgundy font-medium bg-wedding-burgundy/10' 
                      : 'text-gray-700 hover:text-wedding-burgundy hover:bg-wedding-burgundy/5'}`}
                >
                  {section.title}
                </button>
              ))}
              <button
                onClick={() => scrollToSection("rsvp")}
                className={`w-full mt-3 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-300 font-outfit
                  ${isScrolled 
                    ? 'text-white bg-wedding-burgundy hover:bg-wedding-burgundy/90' 
                    : 'text-wedding-burgundy bg-white/90 backdrop-blur-sm hover:bg-white'}`}
              >
                RSVP
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
